import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Zap, Users, MapPin, Building2, HeartPulse, ShieldCheck, GraduationCap, Star, Calendar as CalIcon } from "lucide-react";
import { api, BRAND } from "../lib/api";

const industries = [
  { icon: Building2, label: "Businesses" },
  { icon: GraduationCap, label: "Schools" },
  { icon: HeartPulse, label: "Healthcare" },
  { icon: Users, label: "Childcare" },
  { icon: ShieldCheck, label: "Nonprofits" },
];

export default function Home() {
  const [classes, setClasses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data.slice(0, 3))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-navy text-white">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${BRAND.heroImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="animate-fade-up text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-brand-teal/20 border border-brand-teal/40 text-brand-teal px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest">
              <Zap className="h-3 w-3" /> Serving PA & NJ
            </div>
            <div className="mt-8 flex flex-col lg:flex-row items-center lg:items-end gap-4 lg:gap-8 justify-center lg:justify-start">
              <img src={BRAND.logoCircle} alt="KenMarq Safety & Training" className="h-32 sm:h-40 md:h-48 lg:h-56 w-auto object-contain drop-shadow-2xl shrink-0" data-testid="hero-brand-logo" />
              <div>
                <div className="font-display font-extrabold tracking-tight leading-[0.9] text-5xl sm:text-6xl md:text-7xl lg:text-[110px] text-white" data-testid="hero-brand-name">
                  KENMARQ
                </div>
                <div className="mt-2 text-brand-coral font-display font-bold tracking-widest text-lg sm:text-xl md:text-2xl lg:text-3xl">
                  SAFETY &amp; TRAINING
                </div>
                <div className="mt-2 flex items-center gap-3 justify-center lg:justify-start text-white/70 text-xs sm:text-sm font-semibold tracking-widest uppercase">
                  <span>Train</span><span className="text-brand-teal">•</span><span>Prepare</span><span className="text-brand-amber">•</span><span>Protect</span>
                </div>
              </div>
            </div>
            <h1 className="mt-10 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight max-w-4xl mx-auto lg:mx-0">
              Practical Training. <span className="text-brand-amber">Lifesaving Skills.</span> Confidence When It Matters Most.
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed mx-auto lg:mx-0">
              CPR, First Aid, and safety training for individuals, childcare professionals, businesses, and community organizations throughout Pennsylvania and New Jersey. Attend a class at our Bethlehem training location or bring KenMarq Safety Training directly to your team.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center lg:justify-start">
              <Link to="/classes" data-testid="hero-view-classes">
                <Button size="lg" className="bg-brand-coral hover:bg-brand-coral-dark text-white rounded-full px-8 py-6 text-base font-bold btn-lift">
                  View Upcoming Classes <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/group-training" data-testid="hero-request-training">
                <Button size="lg" variant="outline" className="bg-white/5 border-2 border-brand-teal text-white hover:bg-brand-teal hover:text-white rounded-full px-8 py-6 text-base font-bold btn-lift">
                  Request Group Training
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value strip */}
      <section className="bg-brand-amber/10 border-y border-brand-amber/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
          {[
            { icon: HeartPulse, label: "Hands-on Instruction", color: "text-brand-coral" },
            { icon: Users, label: "Individual & Group Classes", color: "text-brand-teal" },
            { icon: MapPin, label: "On-site Training Available", color: "text-brand-navy" },
            { icon: ShieldCheck, label: "Bethlehem, PA Center", color: "text-brand-amber" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 justify-center md:justify-start">
              <item.icon className={`h-6 w-6 ${item.color}`} />
              <span className="text-sm font-semibold text-brand-navy">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Bento: Who we serve */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5">
            <span className="font-script text-3xl text-brand-coral">Who we serve</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-navy tracking-tight mt-2">Engaging, hands-on training for the people who show up for others.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">From childcare centers meeting state requirements to corporate teams building workplace safety culture, we make lifesaving education clear, enjoyable, and easy to understand.</p>
            <Link to="/services" className="inline-flex items-center gap-1 mt-6 text-brand-teal font-bold hover:gap-2 transition-all" data-testid="home-view-services">
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {industries.map((ind) => (
              <div key={ind.label} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-brand-teal transition-colors" data-testid={`industry-${ind.label.toLowerCase()}`}>
                <ind.icon className="h-8 w-8 text-brand-teal mb-3" />
                <div className="font-display font-bold text-brand-navy">{ind.label}</div>
              </div>
            ))}
            <div className="bg-brand-navy text-white rounded-2xl p-6 flex flex-col justify-between">
              <Building2 className="h-8 w-8 text-brand-amber" />
              <div className="font-display font-bold">Community Groups, Churches, and more</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Classes */}
      <section className="bg-brand-surface py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between mb-10 gap-4">
            <div>
              <span className="font-script text-3xl text-brand-teal">This month</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-navy tracking-tight">Featured upcoming classes</h2>
            </div>
            <Link to="/classes" data-testid="home-see-all-classes">
              <Button variant="outline" className="rounded-full border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white btn-lift font-semibold">See full calendar <CalIcon className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {classes.length === 0 && <p className="text-slate-500">No classes scheduled yet.</p>}
            {classes.map((c) => (
              <ClassCard key={c.id} c={c} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="font-script text-3xl text-brand-coral">Trusted by teams</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-navy tracking-tight">What clients say</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {testimonials.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-md transition-shadow" data-testid={`testimonial-${t.id}`}>
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: t.rating || 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-brand-amber text-brand-amber" />
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed">"{t.quote}"</p>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <div className="font-display font-bold text-brand-navy">{t.author}</div>
                <div className="text-sm text-slate-500">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Ready to train your team?</h2>
            <p className="mt-4 text-white/80 leading-relaxed">Tell us about your organization, preferred dates, and number of participants — we'll help you plan the right class.</p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link to="/group-training" data-testid="cta-group-training">
              <Button size="lg" className="bg-brand-coral hover:bg-brand-coral-dark text-white rounded-full px-8 py-6 font-bold btn-lift">Request a Group Training Quote</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function ClassCard({ c }) {
  const date = new Date(c.date);
  return (
    <Link to={`/classes/${c.id}`} className="group block bg-white border border-slate-200 rounded-2xl p-6 hover:border-brand-teal hover:shadow-md transition-all" data-testid={`class-card-${c.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-brand-teal">{c.training_type}</div>
          <h3 className="mt-2 font-display text-xl font-bold text-brand-navy group-hover:text-brand-teal transition-colors">{c.title}</h3>
        </div>
        <div className="text-center bg-brand-navy text-white rounded-xl p-3 shrink-0">
          <div className="text-xs font-semibold opacity-80">{date.toLocaleString("en-US", { month: "short" }).toUpperCase()}</div>
          <div className="text-2xl font-display font-extrabold">{date.getDate()}</div>
        </div>
      </div>
      <div className="mt-4 text-sm text-slate-600 space-y-1">
        <div>🕐 {c.start_time} – {c.end_time}</div>
        <div>📍 {c.location}</div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-lg font-display font-bold text-brand-navy">${c.price}</div>
          <div className="text-xs text-slate-500">{c.is_full ? "Waitlist only" : `${c.seats_available} seats left`}</div>
        </div>
        <span className="text-brand-coral font-bold text-sm group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">Register <ArrowRight className="h-4 w-4" /></span>
      </div>
    </Link>
  );
}
